import { localStorage as local } from '@entrata/utils'
import parseQueryString from 'portal-modules/common/utils/parse-query-string'
import {
  JWT_STORAGE_KEY,
  SETTINGS_STORAGE_KEY,
  SETUP_DATA_KEY,
  RESET_PASSWORD_KEY,
  CLUSTER_KEY,
  CLIENT_ID_KEY,
  SSO_TOKEN_KEY,
  LOGIN_FROM_ENTRATA,
  IS_COMPANY_USER_FULL_ACCESS,
  WELCOME_MODAL_SHOWN
} from 'portal-modules/common/constants/keys'
import decode from 'jwt-decode'
import { clearSetupDataSync } from 'portal-modules/common/utils/sync-user-data'
import stripTags from 'portal-modules/common/utils/strip-tags'
import {
  getSessionCookies,
  removeSessionCookies,
} from 'portal-utils/session-cookies'
import { getDecryptedCryptoData } from 'portal-modules/common/utils/crypto-encryption'
import getProjectConditions from 'portal-modules/common/utils/get-project-conditions'

const getSessionStorage = () => {
  try {
    return window.sessionStorage;
  } catch (e) {
    console.error('Session storage is not available:', e);
    return {
      getItem: () => null,
      setItem: () => { },
      removeItem: () => { }
    };
  }
};

const decodeUser = (token, { cid }) => {
  let user = {}
  const storage = getSessionStorage();

  if (typeof token === 'string') {
    const { exp, data = {} } = decode(token)
    data['websiteId'] = null
    const { username, readOnly, ...rest } = data

    const rp_reset = storage.getItem(RESET_PASSWORD_KEY)

    if (rp_reset) {
      storage.removeItem(RESET_PASSWORD_KEY)
      removeSessionCookies(JWT_STORAGE_KEY)
      return {}
    }

    // token is expired if current milliseconds > milliseconds from token.exp
    // (we need to multiply token.exp by 1000 to convert UTC timestamp to milliseconds)
    const expired = exp && Date.now() - exp * 1000 > 1
    // if the token is expired or doesn't match the client or website,
    // clear old user data from storage and bail
    if (expired || (cid && rest.cid != cid)) {
      removeSessionCookies(JWT_STORAGE_KEY)
      clearSetupDataSync(username)
      return {}
    }
    user = { ...rest, token, readOnly, username }
    if (username) user.key = username
  }

  return user
}

const getUserSettings = (data = {}, tokenData = {}) => {
  const { key, token, ...rest } = data
  if (tokenData.leaseId) rest.leaseId = tokenData.leaseId
  if (tokenData.propertyId) rest.propertyId = tokenData.propertyId
  return rest
}

const getUserData = data => {
  if (!data) return {}
  return ['leaseId', 'propertyId'].reduce((obj, key) => {
    if (data[key]) return { ...obj, [key]: data[key] }
    return {}
  }, {})
}

export default ({ user = {}, userSettings = {}, ...rest }) => {
  const storage = getSessionStorage();
  const { isTP } = getProjectConditions();
  const { login_error, token, cid, cluster_id, sso_token, login_from_entrata, is_company_user_full_access = 1 } =
    parseQueryString(window.location.href)

  const rp_sd = getDecryptedCryptoData(SETUP_DATA_KEY)
  const rp_us = getSessionCookies(SETTINGS_STORAGE_KEY)
  const rp_at = getSessionCookies(JWT_STORAGE_KEY)

  if (cid) {
    rest['cid'] = cid
    if (isTP) {
      storage.setItem(CLIENT_ID_KEY, cid)
    } else {
      local.set(CLIENT_ID_KEY, cid)
    }
  }

  if (cluster_id) {
    if (isTP) {
      storage.setItem(CLUSTER_KEY, cluster_id)
    } else {
      local.set(CLUSTER_KEY, cluster_id)
    }
  }

  if (login_from_entrata) {
    if (isTP) {
      storage.setItem(LOGIN_FROM_ENTRATA, login_from_entrata)
    } else {
      local.set(LOGIN_FROM_ENTRATA, login_from_entrata)
    }
  }

  // Set is_company_user_full_access if it doesn't already exist (preserve existing values on refresh)
  if (storage.getItem(IS_COMPANY_USER_FULL_ACCESS) === null) {
    storage.setItem(IS_COMPANY_USER_FULL_ACCESS, is_company_user_full_access);
    storage.setItem(WELCOME_MODAL_SHOWN, 'true');
  }
  
  sso_token && sso_token != '' && storage.setItem(SSO_TOKEN_KEY, sso_token)

  const decodedToken = decodeUser(token || rp_at, rest)
  const setupData = (rp_sd && decodedToken.key && rp_sd[decodedToken.key]) || {}
  const settings = (rp_us && decodedToken.key && rp_us[decodedToken.key]) || {}
  let initialState = { ...rest }

  // if we have a property already selected, set the timezone for i18n
  if (decodedToken.propertyId && setupData.properties) {
    const property = setupData.properties[decodedToken.propertyId] || {}
    if (property.timezone) i18nUtils.setTimezone(property.timezone)
  }
  // add any login errors if passed from failed third party attempt
  if (login_error) {
    initialState.errors = {
      login: stripTags(decodeURI(login_error)),
    }
  }

  return {
    ...initialState,
    ...setupData,
    user: {
      ...user,
      ...getUserData(settings),
      ...decodedToken,
    },
    userSettings: {
      ...getUserSettings(settings, decodedToken),
      ...userSettings,
    },
    ...rest,
  }
}
